import AddCircle from '@mui/icons-material/AddCircle';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import LinkIcon from '@mui/icons-material/Link';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import { Divider, Tooltip, Typography } from '@mui/material';
import Box from '@mui/material/Box';
import React, { SyntheticEvent, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { CHECKBOX, CREATE_SURVEY, FREE_TEXT, MATRICE, RADIO, TABLE, UPDATE_SURVEY } from '../../constants';
import {
  generateRandomString,
  getFilteredCodes,
  getSurveyItem,
  resetBlockQuestion,
  swapColumns,
  swapItems,
} from '../../helpers/surveyUtil';
import { BlockProps, ChapterProps, ColumnProps, IdName, RowProps, SectionProps } from '../../models/global';
import { CodeInterface, State } from '../../models/state';
import Dropdown from '../FormComponents/Dropdown';
import TextField from '../FormComponents/TextField';
import ToastMessage from '../ToastMessage/ToastMessage';
import Field from './Field';
import { InterrelatedFieldRef } from './InterrelatedField';

interface FieldBlockProps {
  block: BlockProps;
  section: SectionProps;
  chapter: ChapterProps;
  chapters: ChapterProps[];
  index: number;
  language: string;
  modalRef: React.MutableRefObject<InterrelatedFieldRef>;
  invalidFields: string[];
  readonly?: boolean;
  type: typeof CREATE_SURVEY | typeof UPDATE_SURVEY;
  forceToUpdate: () => void;
}

const FieldBlock = React.memo(
  ({
    block,
    section,
    chapter,
    index,
    language,
    modalRef,
    invalidFields,
    chapters,
    readonly,
    type,
    forceToUpdate,
  }: FieldBlockProps) => {
    const [toast, setToast] = useState('');
    const [blockData, setBlockData] = useState<BlockProps>(block);
    const { fieldTypes } = useSelector((state: State) => state.survey);
    const { codes } = useSelector((state: State) => state.code);

    useEffect(() => {
      setBlockData(block);
    }, [block]);

    const getRelatedField = (optionIds: string[] = [], section: SectionProps) => {
      const dependentQuestionIndex = section.fieldBlocks.findIndex(fieldBlock => {
        const fieldBlockOptions = fieldBlock.options?.map(el => el.id) || [];
        return optionIds.some(optionId => fieldBlockOptions.includes(optionId));
      });
      if (dependentQuestionIndex === -1) {
        return;
      }
      return {
        dependentQuestion: section.fieldBlocks[dependentQuestionIndex],
        dependentQuestionIndex,
      };
    };

    const getFilteredFieldTypes = (fieldBlocksCount: number) => {
      if (fieldBlocksCount > 1) {
        return fieldTypes.filter(fieldType => ![TABLE, MATRICE].includes(fieldType));
      }
      return fieldTypes;
    };

    const handleQuestion = (value: string, chapterId: string, sectionId: string, blockId: string) => {
      const block = getSurveyItem(chapters, chapterId, sectionId, blockId) as BlockProps;
      const key = language === 'English' ? 'question' : `question${language}`;
      block[key] = value;
      setBlockData({ ...block });
    };

    const handleCode = (value: CodeInterface, chapterId: string, sectionId: string, blockId: string) => {
      const block = getSurveyItem(chapters, chapterId, sectionId, blockId) as BlockProps;
      block.codeId = value?.codeId || null;
      setBlockData({ ...block });
    };

    const handleOtherOption = (chapterId: string, sectionId: string, blockId: string) => {
      const block = getSurveyItem(chapters, chapterId, sectionId, blockId) as BlockProps;
      block.hasOtherOption = !block.hasOtherOption;
      setBlockData({ ...block });
    };

    const handleOptions = (value: string, chapterId: string, sectionId: string, blockId: string, optionId: string) => {
      const block = getSurveyItem(chapters, chapterId, sectionId, blockId) as BlockProps;
      if (!block.options) {
        block.options = [];
      }
      const option = block.options.find(el => el.id === optionId);
      if (option) {
        const key = language === 'English' ? 'name' : `name${language}`;
        option[key] = value;
      }
      setBlockData({ ...block });
    };

    const addOption = (chapterId: string, sectionId: string, blockId: string) => {
      const block = getSurveyItem(chapters, chapterId, sectionId, blockId) as BlockProps;
      if (!block.options) {
        block.options = [];
      }
      block.options.push({ id: generateRandomString(true), name: '' });
      setBlockData({ ...block });
    };

    const removeOption = (chapterId: string, blockId: string, sectionId: string, index: number) => {
      const block = getSurveyItem(chapters, chapterId, sectionId, blockId) as BlockProps;
      block.options = block.options.filter((_, i) => i !== index);
      setBlockData({ ...block });
    };

    const addBlock = (chapterId: string, sectionId: string) => {
      const section = getSurveyItem(chapters, chapterId, sectionId) as SectionProps;
      section.fieldBlocks.push({
        question: '',
        fieldType: '',
        id: generateRandomString(true),
        codeId: null,
        order: section.fieldBlocks.length + 1,
      });
      forceToUpdate();
    };

    const removeBlock = (chapterId: string, sectionId: string, blockId: string) => {
      const section = getSurveyItem(chapters, chapterId, sectionId) as SectionProps;
      section.fieldBlocks = section.fieldBlocks.filter(el => el.id !== blockId);
      let order = 1;
      section.fieldBlocks.forEach(fieldBlock => {
        fieldBlock.order = order;
        order++;
      });
      forceToUpdate();
    };

    const handleFieldTypeDropdown = (value: string, chapterId: string, sectionId: string, blockId: string) => {
      const block = getSurveyItem(chapters, chapterId, sectionId, blockId) as BlockProps;
      if (block) {
        block.fieldType = value;
        if ([CHECKBOX, RADIO].includes(value)) {
          block.options = [{ id: generateRandomString(true), name: '' }];
        }
        if ([CHECKBOX, RADIO, FREE_TEXT].includes(value)) {
          delete block.table;
          delete block.matrice;
        }
        if ([TABLE].includes(value) && !block.table?.columns) {
          block.table = {
            id: generateRandomString(true),
            columns: [],
          };
          delete block.options;
          delete block.matrice;
          resetBlockQuestion(block);
        }
        if ([MATRICE].includes(value) && !block.matrice?.columns) {
          block.matrice = {
            id: generateRandomString(true),
            columns: [],
            rows: [],
          };
          delete block.options;
          delete block.table;
          block.codeId = null;
          resetBlockQuestion(block);
        }
        if (!value) {
          delete block.options;
          delete block.table;
          delete block.matrice;
        }
        setBlockData({ ...block });
      }
    };

    const addRow = (chapterId: string, sectionId: string, blockId: string) => {
      const block = getSurveyItem(chapters, chapterId, sectionId, blockId) as BlockProps;
      if (!block.matrice.rows) {
        block.matrice.rows = [];
      }
      (block.matrice.rows as RowProps[]).push({
        id: generateRandomString(true),
        name: '',
        codeId: null,
        rowCells: [],
      });
      setBlockData({ ...block });
    };

    const removeRow = (chapterId: string, sectionId: string, blockId: string, rowId: string) => {
      const block = getSurveyItem(chapters, chapterId, sectionId, blockId) as BlockProps;
      block.matrice.rows = (block.matrice.rows as RowProps[]).filter(row => row.id !== rowId);
      setBlockData({ ...block });
    };

    const changeColumnTypes = (chapterId: string, sectionId: string, blockId: string, columnType: string) => {
      const block = getSurveyItem(chapters, chapterId, sectionId, blockId) as BlockProps;
      (block.matrice.columns as ColumnProps[]).forEach(column => {
        if (column.type !== FREE_TEXT) {
          column.type = columnType;
        }
      });
      setBlockData({ ...block });
    };

    const addColumn = (
      chapterId: string,
      sectionId: string,
      blockId: string,
      columnType: string,
      type: 'table' | 'matrice',
    ) => {
      const block = getSurveyItem(chapters, chapterId, sectionId, blockId) as BlockProps;
      const columnId = generateRandomString(true);

      if (!block[type]) {
        if (type === 'matrice') {
          block[type] = {
            id: generateRandomString(true),
            columns: [],
            rows: [],
          };
        } else {
          block[type] = { id: generateRandomString(true), columns: [] };
        }
      }

      if (!block[type].columns) {
        block[type].columns = [];
      }

      if (type === 'matrice') {
        const otherOptionColumn = (block[type].columns as ColumnProps[]).find(col => col.type === FREE_TEXT);
        if (otherOptionColumn && columnType !== FREE_TEXT) {
          const newColumn = {
            id: columnId,
            type: columnType,
            name: '',
          };
          swapColumns(block[type].columns, newColumn);
        } else {
          (block[type].columns as ColumnProps[]).push({
            id: columnId,
            name: `Col ${block[type].columns.length + 1}`,
            type: columnType,
          });
        }

        if (!block[type].rows?.length) {
          addRow(chapterId, sectionId, blockId);
        }
      } else {
        (block[type].columns as ColumnProps[]).push({
          id: columnId,
          name: `Col ${block[type].columns.length + 1}`,
          type: columnType,
        });
      }

      setBlockData({ ...block });
    };

    const removeColumn = (
      chapterId: string,
      sectionId: string,
      blockId: string,
      colId: string,
      type: 'table' | 'matrice',
    ) => {
      const block = getSurveyItem(chapters, chapterId, sectionId, blockId) as BlockProps;
      block[type].columns = (block[type].columns as ColumnProps[]).filter(column => column.id !== colId);
      if (!block[type].columns.length && type === 'matrice') {
        block[type].rows = [];
      }

      setBlockData({ ...block });
    };

    const handleColumnName = (
      value: string,
      chapterId: string,
      sectionId: string,
      blockId: string,
      colId: string,
      type: 'table' | 'matrice',
    ) => {
      const block = getSurveyItem(chapters, chapterId, sectionId, blockId) as BlockProps;
      const column = block[type].columns.find((col: ColumnProps) => col.id === colId);
      const key = language === 'English' ? 'name' : `name${language}`;
      column[key] = value;

      setBlockData({ ...block });
    };

    const handleMatrixRowName = (
      value: string,
      chapterId: string,
      sectionId: string,
      blockId: string,
      rowId: string,
    ) => {
      const block = getSurveyItem(chapters, chapterId, sectionId, blockId) as BlockProps;
      const row = block.matrice.rows.find((row: RowProps) => row.id === rowId);
      const key = language === 'English' ? 'name' : `name${language}`;
      row[key] = value;

      setBlockData({ ...block });
    };

    const handleMatrixRowCode = (
      value: CodeInterface,
      chapterId: string,
      sectionId: string,
      blockId: string,
      rowId: string,
    ) => {
      const block = getSurveyItem(chapters, chapterId, sectionId, blockId) as BlockProps;
      const row = block.matrice.rows.find((row: RowProps) => row.id === rowId);
      row.codeId = value?.codeId || null;

      setBlockData({ ...block });
    };

    const swapOptions = (
      chapterId: string,
      sectionId: string,
      blockId: string,
      optionIndex: number,
      direction: 'prev' | 'next',
    ) => {
      const block = getSurveyItem(chapters, chapterId, sectionId, blockId) as BlockProps;
      swapItems(block.options, optionIndex, direction);

      setBlockData({ ...block });
    };

    const swapFieldBlocks = (chapterId: string, sectionId: string, blockIndex: number, direction: 'prev' | 'next') => {
      const section = getSurveyItem(chapters, chapterId, sectionId) as SectionProps;
      const result = swapItems(
        section.fieldBlocks,
        blockIndex,
        direction,
        relatedField?.dependentQuestionIndex + 1,
        'fieldBlock',
      );
      if (!result) {
        setToast('The questions are interrelated so you can not reorder them.');
        return;
      }

      forceToUpdate();
    };

    const relatedField = getRelatedField(
      block.dependsOnOptions || block.interrelatedFields?.map(el => el.optionId),
      section,
    );

    return (
      !!block && (
        <Box key={block.id} id={block.id}>
          {!!relatedField && (
            <Typography className='related-question-info'>
              This question is dependent on{' '}
              <span
                className='related-question'
                onClick={() => {
                  const relatedQuestion = document.getElementById(relatedField.dependentQuestion.id);
                  if (relatedQuestion) {
                    relatedQuestion.scrollIntoView({
                      behavior: 'smooth',
                    });
                  }
                }}
              >
                {relatedField.dependentQuestion.question}
              </span>
            </Typography>
          )}
          <Box
            key={index}
            display='flex'
            gap={1}
            mr={1}
            style={language !== 'English' ? { alignItems: 'flex-start' } : {}}
          >
            <Box display='flex' flexDirection='column' gap={0.5} flex='0 0 45%'>
              <Box display='flex' gap={0.5}>
                {type === UPDATE_SURVEY && (
                  <Typography display='inline' fontWeight={700}>
                    {chapter.order}.{section.order}.{block.order}
                  </Typography>
                )}
                <TextField
                  label='Question'
                  sx={{ width: '100%' }}
                  maxLength={600}
                  multiline
                  value={language === 'English' ? blockData.question : `${blockData[`question${language}`] || ''}`}
                  onChange={e => handleQuestion(e.target.value, chapter.id, section.id, block.id)}
                  error={
                    ![TABLE, MATRICE].includes(block.fieldType) &&
                    invalidFields.includes(`QUESTION_${block.id}`) &&
                    language === 'English'
                  }
                  required={![TABLE, MATRICE].includes(block.fieldType)}
                  disabled={[TABLE, MATRICE].includes(block.fieldType)}
                />
              </Box>
              {language !== 'English' && block.fieldType !== TABLE && (
                <Typography className='english-value'>English: {block.question || 'N/A'}</Typography>
              )}
            </Box>
            <Box display='flex' alignItems='center' flex='0 0 30%'>
              <Dropdown
                value={codes.find(code => code.codeId === blockData.codeId)?.code || ''}
                isFullWidth
                label='Code'
                options={getFilteredCodes(chapters, codes).map(({ code, codeId }) => ({
                  id: codeId,
                  name: code,
                }))}
                onChange={(_: SyntheticEvent, value: IdName) => {
                  handleCode(
                    value ? { codeId: Number(value.id), code: value.name } : null,
                    chapter.id,
                    section.id,
                    block.id,
                  );
                }}
                error={![MATRICE].includes(block.fieldType) && invalidFields.includes(`CODE_${block.id}`)}
                required={![MATRICE].includes(block.fieldType)}
                disabled={[MATRICE].includes(block.fieldType)}
              />
            </Box>
            <Box display='flex' alignItems='center' flex='0 0 25%' gap={0.5}>
              <Dropdown
                value={block.fieldType}
                label='Type'
                isFullWidth
                options={getFilteredFieldTypes(section.fieldBlocks.length)}
                onChange={(_: SyntheticEvent, value: string) =>
                  handleFieldTypeDropdown(value, chapter.id, section.id, block.id)
                }
                error={invalidFields.includes(`FIELD_TYPE_${block.id}`) && language === 'English'}
                required
              />
              {![TABLE, MATRICE].includes(block.fieldType) &&
                (index === 0 ? (
                  <AddCircle htmlColor='green' onClick={() => addBlock(chapter.id, section.id)} />
                ) : (
                  <RemoveCircleIcon htmlColor='red' onClick={() => removeBlock(chapter.id, section.id, block.id)} />
                ))}
              {index !== 0 && (
                <LinkIcon
                  onClick={() => modalRef.current.handleModal(chapter.id, section.id, index, readonly)}
                  sx={{ pointerEvents: 'visible' }}
                />
              )}
              <>
                {index !== section.fieldBlocks.length - 1 && (
                  <Tooltip title={'Swap with the next one'}>
                    <ArrowDownwardIcon
                      fontSize='small'
                      onClick={() => swapFieldBlocks(chapter.id, section.id, index, 'next')}
                    />
                  </Tooltip>
                )}
                {index !== 0 && (
                  <Tooltip title={'Swap with the previous one'}>
                    <ArrowUpwardIcon
                      fontSize='small'
                      onClick={() => swapFieldBlocks(chapter.id, section.id, index, 'prev')}
                    />
                  </Tooltip>
                )}
              </>
            </Box>
          </Box>
          <Box sx={{ mb: 2.5 }}>
            <Field
              chapterId={chapter.id}
              sectionId={section.id}
              block={blockData}
              handleOptions={handleOptions}
              addOption={addOption}
              removeOption={removeOption}
              invalidFields={invalidFields}
              handleColumnName={handleColumnName}
              handleMatrixRowName={handleMatrixRowName}
              handleMatrixRowCode={handleMatrixRowCode}
              addColumn={addColumn}
              addRow={addRow}
              removeColumn={removeColumn}
              removeRow={removeRow}
              handleOtherOption={handleOtherOption}
              swapOptions={swapOptions}
              changeColumnTypes={changeColumnTypes}
              language={language}
              readonly={readonly}
              filteredCodes={getFilteredCodes(chapters, codes)}
            />
            {![TABLE, MATRICE].includes(block.fieldType) && <Divider sx={{ mt: 4 }} />}
          </Box>
          <ToastMessage toast={toast} close={() => setToast('')} messageType='info' />
        </Box>
      )
    );
  },
);

FieldBlock.displayName = 'FieldBlock';

export default FieldBlock;
